import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { AuthModule, AuthHttpInterceptor } from '@auth0/auth0-angular';
import { DbContextInterceptor } from './core/db-context.interceptor';
import { LazySentryErrorHandler } from './core/lazy-sentry';
import { DemoApiInterceptor } from './demo/demo-api.interceptor';
import { environment } from '../environments/environment';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    AuthModule.forRoot({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.auth0.audience,
      },
      // Persist the session across refreshes: cache tokens in localStorage and
      // renew them with refresh tokens (with the iframe fallback if the API
      // has no offline access). Without this the SDK is in-memory only and the
      // user appears logged out after every reload.
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      // Attach the access token ONLY to the authenticated endpoints. The public
      // endpoints (/demo/* live-demo crawl, /sources registry) are deliberately
      // NOT listed: anything outside this list is forwarded untouched, so it
      // never waits on Auth0 — the "try it" demo works with no login at all.
      httpInterceptor: {
        allowedList: [
          `${environment.apiBaseUrl}/activities*`,
          `${environment.apiBaseUrl}/crawl*`,
          `${environment.apiBaseUrl}/refresh*`,
          `${environment.apiBaseUrl}/profile*`,
          `${environment.apiBaseUrl}/billing*`,
          // Insights feed, card actions, and the manual "refresh insights" run.
          // Without these the token is never attached and every insights call
          // 401s, which the feed surfaces as "we couldn't load your insights".
          `${environment.apiBaseUrl}/insights*`,
          `${environment.apiBaseUrl}/analyze*`,
        ],
      },
    }),
  ],
  providers: [
    provideHotToastConfig(),
    provideHttpClient(withInterceptorsFromDi()),
    // FIRST in the chain: in demo mode it answers every API call from the
    // in-memory store, before Auth0 can demand a token. Pass-through otherwise.
    { provide: HTTP_INTERCEPTORS, useClass: DemoApiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    // Stamps the env's db + collection headers on every API request.
    { provide: HTTP_INTERCEPTORS, useClass: DbContextInterceptor, multi: true },
    // Console-first error handler; forwards to the lazily-loaded Sentry on
    // live hosts only — keeps the SDK out of the initial bundle entirely.
    { provide: ErrorHandler, useClass: LazySentryErrorHandler },
  ],
})
export class AppModule {}
